import React, { useEffect, useState } from "react";
import { agency } from "../api";

/**
 * Agency-admin-only Errors tab.
 *
 * Shows persistent error_logs entries with filters, expand-row to see the
 * full traceback + context, and an inline resolve form. Errors land here
 * automatically from collectors, narrator, recommender, the API
 * middleware, and the cron scripts.
 */
const SOURCES = ["", "collector", "narrator", "recommender", "api", "cron"];
const SEVERITIES = ["", "critical", "error", "warning"];
const STATUSES = ["open", "resolved", "all"];

export default function AgencyErrors() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ status: "open", source: "", severity: "" });
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setErr(null);
    try {
      const params = { status: filters.status };
      if (filters.source) params.source = filters.source;
      if (filters.severity) params.severity = filters.severity;
      const [s, r] = await Promise.all([agency.errorStats(), agency.errors(params)]);
      setStats(s);
      setRows(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  async function expand(id) {
    if (expandedId === id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(id);
    setDetail(null);
    try {
      setDetail(await agency.errorDetail(id));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    }
  }

  async function resolve(id, resolved, notes) {
    setBusy(true);
    try {
      await agency.resolveError(id, { resolved, resolution_notes: notes || null });
      await load();
      if (expandedId === id) setDetail(await agency.errorDetail(id));
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Errors</h1>
          {stats && (
            <div className="subtle">
              {stats.total_open} unresolved · {stats.last_24h} in last 24h
              {stats.by_severity?.critical ? ` · ${stats.by_severity.critical} critical` : ""}
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="grid grid-3">
          <div>
            <label>Status</label>
            <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label>Source</label>
            <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
              {SOURCES.map((s) => <option key={s} value={s}>{s || "(any)"}</option>)}
            </select>
          </div>
          <div>
            <label>Severity</label>
            <select value={filters.severity} onChange={(e) => setFilters({ ...filters, severity: e.target.value })}>
              {SEVERITIES.map((s) => <option key={s} value={s}>{s || "(any)"}</option>)}
            </select>
          </div>
        </div>
      </div>

      {err && <div className="error-box">{err}</div>}

      {rows == null ? (
        <p className="subtle">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="empty">
          {filters.status === "open" ? "No open errors. ✓" : "No errors match these filters."}
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="data" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th></th>
                <th>When</th>
                <th>Source</th>
                <th>Component</th>
                <th>Client</th>
                <th>Message</th>
                <th>Count</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr style={{ cursor: "pointer" }} onClick={() => expand(r.id)}>
                    <td><SeverityDot severity={r.severity} /></td>
                    <td>{relativeTime(r.last_seen_at)}</td>
                    <td><span className="tag">{r.source}</span></td>
                    <td style={{ fontSize: 13, color: "var(--ink-soft)" }}>{r.component || "—"}</td>
                    <td>{r.client_name || <span className="subtle">system</span>}</td>
                    <td style={{ maxWidth: 480, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {r.message}
                    </td>
                    <td style={{ textAlign: "right" }}>
                      {r.occurrence_count > 1 && <span className="tag pill-accent">×{r.occurrence_count}</span>}
                    </td>
                    <td>{r.resolved ? <span className="tag low">resolved</span> : <span className="tag high">open</span>}</td>
                  </tr>
                  {expandedId === r.id && (
                    <tr>
                      <td colSpan={8} style={{ background: "var(--surface-2)" }}>
                        <ErrorDetailPanel detail={detail} onResolve={resolve} busy={busy} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SeverityDot({ severity }) {
  const klass =
    severity === "critical" ? "dot-bad" :
    severity === "error" ? "dot-bad" :
    severity === "warning" ? "dot-warn" :
    "dot-off";
  return <span className={`dot ${klass}`} title={severity} />;
}

function ErrorDetailPanel({ detail, onResolve, busy }) {
  const [notes, setNotes] = useState("");

  if (!detail) return <div className="subtle" style={{ padding: 12 }}>Loading…</div>;

  return (
    <div style={{ padding: 14 }}>
      <div className="grid grid-2" style={{ gap: 14 }}>
        <div>
          <h3 style={{ marginBottom: 6 }}>Message</h3>
          <pre style={preStyle}>{detail.message}</pre>

          {detail.traceback && (
            <>
              <h3 style={{ marginTop: 14, marginBottom: 6 }}>Traceback</h3>
              <pre style={{ ...preStyle, maxHeight: 360, overflow: "auto" }}>{detail.traceback}</pre>
            </>
          )}
        </div>

        <div>
          <h3 style={{ marginBottom: 6 }}>Context</h3>
          {detail.context && Object.keys(detail.context).length > 0 ? (
            <pre style={preStyle}>{JSON.stringify(detail.context, null, 2)}</pre>
          ) : <div className="empty">No context.</div>}

          <h3 style={{ marginTop: 14, marginBottom: 6 }}>Resolution</h3>
          {detail.resolved ? (
            <div>
              <p className="subtle">
                Resolved {relativeTime(detail.resolved_at)}
                {detail.resolved_by_name ? ` by ${detail.resolved_by_name}` : ""}
              </p>
              {detail.resolution_notes && <p>{detail.resolution_notes}</p>}
              <button
                className="secondary" disabled={busy}
                onClick={() => onResolve(detail.id, false, null)}
              >
                Re-open
              </button>
            </div>
          ) : (
            <div>
              <textarea
                rows={2}
                placeholder="Resolution notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <div style={{ marginTop: 8 }}>
                <button disabled={busy} onClick={() => onResolve(detail.id, true, notes)}>
                  {busy ? "Saving…" : "Mark resolved"}
                </button>
              </div>
            </div>
          )}

          <div className="subtle" style={{ marginTop: 14, fontSize: 12 }}>
            fingerprint: <code>{detail.fingerprint.slice(0, 12)}…</code>
            {" · "}
            first seen {new Date(detail.occurred_at).toLocaleString()}
            {detail.occurrence_count > 1 && ` · ${detail.occurrence_count} occurrences`}
          </div>
        </div>
      </div>
    </div>
  );
}

const preStyle = {
  background: "var(--surface)",
  padding: 10,
  borderRadius: 6,
  fontSize: 12,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  border: "1px solid var(--line)",
  margin: 0,
};

function relativeTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (diff < hr) return `${Math.max(1, Math.round(diff / min))} min ago`;
  if (diff < day) return `${Math.round(diff / hr)} hr ago`;
  return `${Math.round(diff / day)} days ago`;
}
